import { apiClient } from './apiClient';
import type { ApiRequestOptions } from '../types/api';
import type { WorkDetail } from '../components/Profile/work/WorkDetailModal';
import { uploadMedia, type ProgressCallback, type UploadProgress } from './mediaUploadService';
import { userService } from './userService';
import { resolveAvatarUrl } from '../constants/assets';

export type WorkAuditStatus = 'WAITING_UPLOAD' | 'PENDING' | 'REJECTED' | 'APPROVED';
export type WorkType = 'video' | 'image';
export type PostInteractionType = 'like' | 'bookmark';
export type WorkVisibility = 'public' | 'private' | 'unlisted' | 'draft';
export type WorkLifecycleStatus = 'active' | 'archived' | 'pending_upload' | 'under_review' | 'rejected' | 'deleted';
export type WorkSubmitMode = 'draft' | 'publish';
export type WorkUploadState = 'uploading' | 'processing' | 'failed';

export interface WorkCollaborator {
  id: string;
  name: string;
  avatar: string;
  username?: string;
  verified?: boolean;
}

export interface WorkCreatorSummary {
  id: string;
  name: string;
  avatar: string;
  username?: string;
  verified?: boolean;
}

export interface SpriteSheetConfig {
  columns: number;
  rows: number;
  frameCount: number;
  frameWidth?: number;
  frameHeight?: number;
  sheets?: Array<{
    url: string;
    startFrame: number;
    endFrame: number;
  }>;
}

interface BackendSpriteSheetConfig {
  columns: number;
  rows: number;
  totalFrames: number;
  frameWidth?: number;
  frameHeight?: number;
  sheets?: Array<{
    url: string;
    startFrame: number;
    endFrame: number;
  }>;
}

const PENDING_WORK_UPLOADS_CHANGED_EVENT = 'pending_work_uploads_changed';
const BACKGROUND_WORK_UPLOAD_COMPLETED_EVENT = 'background_work_upload_completed';
const BACKGROUND_WORK_UPLOAD_FAILED_EVENT = 'background_work_upload_failed';
const PENDING_WORK_UPLOADS_STORAGE_KEY = 'craft_cloud_pending_work_uploads';

interface BackendPostSummary {
  postId: string;
  creatorUserId: string;
  title: string;
  description: string;
  postType: WorkType;
  visibility: WorkVisibility;
  status: WorkLifecycleStatus;
  tags: string[];
  thumbnailUrl?: string;
  mediaUrl?: string;
  images?: string[];
  pinned: boolean;
  pinnedAt?: number;
  createdAt: number;
  updatedAt: number;
  publishedAt?: number;
  stats: {
    viewCount: number;
    likeCount: number;
    bookmarkCount: number;
    commentCount: number;
  };
  auditStatus: WorkAuditStatus;
  duration?: number;
  spriteSheetUrl?: string;
  spriteSheetConfig?: SpriteSheetConfig;
  creator?: {
    userId: string;
    displayName: string;
    username: string;
    avatarUrl?: string;
    verified?: boolean;
  };
  userInteraction?: {
    liked: boolean;
    bookmarked: boolean;
  };
}

interface BackendPostDetailResponse {
  post: {
    postId: string;
    creatorUserId: string;
    title: string;
    description: string;
    postType: WorkType;
    visibility: WorkVisibility;
    status: WorkLifecycleStatus;
    tags: string[];
    thumbnailUrl?: string;
    pinned: boolean;
    pinnedAt?: number;
    createdAt: number;
    updatedAt: number;
    publishedAt?: number;
    metadata?: {
      collaborators?: string[];
    };
  };
  creator: {
    userId: string;
    displayName: string;
    username: string;
    avatarUrl?: string;
    verified: boolean;
  };
  media: Array<{
    mediaId: string;
    mediaType: WorkType;
    fileUrl: string;
    thumbnailUrl?: string;
    displayOrder: number;
    duration?: number;
    metadata?: {
      spriteSheet?: BackendSpriteSheetConfig;
    };
  }>;
  stats: {
    viewCount: number;
    likeCount: number;
    commentCount: number;
    bookmarkCount: number;
  };
  userInteraction?: {
    liked: boolean;
    bookmarked: boolean;
    viewed: boolean;
  };
  collaborators?: Array<{
    userId: string;
    displayName: string;
    username: string;
    avatarUrl?: string;
    verified?: boolean;
  }>;
}

interface BackendComment {
  commentId: string;
  content: string;
  likeCount: number;
  liked?: boolean;
  createdAt: number;
  author?: {
    userId: string;
    displayName: string;
    avatarUrl?: string;
    location?: string;
  };
  replies?: BackendComment[];
}

interface BackendCommentsResponse {
  comments: BackendComment[];
  totalCount?: number;
}

interface CreateCommentResponse extends BackendComment {}

interface DeleteCommentResponse {
  deletedCount: number;
  deletedCommentId: string;
  postId: string;
}

export interface WorkSummary {
  id: string;
  creatorUserId: string;
  type: WorkType;
  videoUrl?: string;
  images?: string[];
  thumbnailUrl: string;
  title: string;
  description: string;
  tags: string[];
  likes: number;
  views: number;
  favorites: number;
  commentsCount: number;
  createdAt: string;
  createdAtTimestamp?: number;
  isPinned?: boolean;
  pinnedAt?: number;
  auditStatus?: WorkAuditStatus;
  visibility?: WorkVisibility;
  status?: WorkLifecycleStatus;
  liked?: boolean;
  bookmarked?: boolean;
  collaborators?: WorkCollaborator[];
  creator?: WorkCreatorSummary;
  duration?: number;
  spriteSheetUrl?: string;
  spriteSheetConfig?: SpriteSheetConfig;
  uploadState?: WorkUploadState;
  uploadProgress?: number;
}

type WorkDraftPayload = {
  title: string;
  description: string;
  tags?: string[];
  categories?: string[];
  collaborators?: WorkCollaborator[];
  workType?: WorkType;
  videoFile?: File | null;
  imageFiles?: File[];
  coverFile?: File | null;
  coverPreview?: string;
  coverImageIndex?: number;
};

type PublishWorkOptions = {
  background?: boolean;
  creator?: {
    id: string;
    name: string;
    avatar: string;
  };
  existingWork?: Partial<WorkSummary>;
};

const isBrowser = typeof window !== 'undefined';
const pendingWorkUploads = new Map<string, WorkSummary>();
let persistPendingWorkUploadsTimer: number | undefined;
let currentCommentAuthorCache:
  | {
      userId: string;
      displayName: string;
      avatarUrl?: string;
      location?: string;
    }
  | null = null;

const isLocalPreviewUrl = (url?: string) => !!url && (url.startsWith('blob:') || url.startsWith('data:'));

const hasLocalPreviewMedia = (work: Partial<WorkSummary>) => (
  isLocalPreviewUrl(work.thumbnailUrl) ||
  isLocalPreviewUrl(work.videoUrl) ||
  Boolean(work.images?.some((image) => isLocalPreviewUrl(image)))
);

const loadPendingWorkUploadsFromStorage = () => {
  if (!isBrowser) {
    return;
  }

  try {
    const storedValue = window.localStorage.getItem(PENDING_WORK_UPLOADS_STORAGE_KEY);
    if (!storedValue) {
      return;
    }

    const parsed = JSON.parse(storedValue);
    if (!Array.isArray(parsed)) {
      window.localStorage.removeItem(PENDING_WORK_UPLOADS_STORAGE_KEY);
      return;
    }

    parsed.forEach((item) => {
      if (!item || typeof item !== 'object' || !item.id) {
        return;
      }

      const pendingItem = item as WorkSummary;
      const wasInterruptedUpload = Boolean(pendingItem.uploadState);
      const hasEphemeralPreview = hasLocalPreviewMedia(pendingItem);

      if (wasInterruptedUpload || hasEphemeralPreview) {
        return;
      }

      if (item && typeof item === 'object' && item.id) {
        pendingWorkUploads.set(String(item.id), item as WorkSummary);
      }
    });

    persistPendingWorkUploads();
  } catch (error) {
    console.error('Failed to load pending work uploads from storage:', error);
    window.localStorage.removeItem(PENDING_WORK_UPLOADS_STORAGE_KEY);
  }
};

const persistPendingWorkUploads = () => {
  if (!isBrowser) {
    return;
  }

  try {
    const uploads = Array.from(pendingWorkUploads.values());
    if (uploads.length === 0) {
      window.localStorage.removeItem(PENDING_WORK_UPLOADS_STORAGE_KEY);
      return;
    }

    window.localStorage.setItem(PENDING_WORK_UPLOADS_STORAGE_KEY, JSON.stringify(uploads));
  } catch (error) {
    console.error('Failed to persist pending work uploads:', error);
  }
};

const schedulePendingWorkUploadsPersist = () => {
  if (!isBrowser) {
    return;
  }

  if (typeof persistPendingWorkUploadsTimer === 'number') {
    window.clearTimeout(persistPendingWorkUploadsTimer);
  }

  persistPendingWorkUploadsTimer = window.setTimeout(() => {
    persistPendingWorkUploadsTimer = undefined;
    persistPendingWorkUploads();
  }, 150);
};

loadPendingWorkUploadsFromStorage();

const formatDate = (timestamp?: number) => {
  if (!timestamp) return '';
  return new Date(timestamp).toISOString().slice(0, 10);
};

const emitPendingWorkUploadsChanged = () => {
  if (!isBrowser) {
    return;
  }
  window.dispatchEvent(new Event(PENDING_WORK_UPLOADS_CHANGED_EVENT));
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const setPendingWorkUpload = (work: WorkSummary) => {
  pendingWorkUploads.set(String(work.id), work);
  persistPendingWorkUploads();
  emitPendingWorkUploadsChanged();
};

const updatePendingWorkUpload = (postId: string, patch: Partial<WorkSummary>) => {
  const existing = pendingWorkUploads.get(postId);
  if (!existing) return;

  pendingWorkUploads.set(postId, { ...existing, ...patch });
  schedulePendingWorkUploadsPersist();
  emitPendingWorkUploadsChanged();
};

const removePendingWorkUpload = (postId: string) => {
  if (!pendingWorkUploads.delete(postId)) {
    return;
  }

  persistPendingWorkUploads();
  emitPendingWorkUploadsChanged();
};

const removePendingWorkUploads = (postIds: string[]) => {
  let changed = false;
  postIds.forEach((postId) => {
    if (pendingWorkUploads.delete(postId)) {
      changed = true;
    }
  });

  if (changed) {
    persistPendingWorkUploads();
    emitPendingWorkUploadsChanged();
  }
};

const hasPendingWorkSynced = (pending: WorkSummary, server: WorkSummary) => (
  pending.title === server.title &&
  pending.description === server.description &&
  pending.type === server.type &&
  pending.visibility === server.visibility &&
  pending.status === server.status &&
  JSON.stringify(pending.tags || []) === JSON.stringify(server.tags || [])
);

const hasRenderableMedia = (work: Partial<WorkSummary>) => {
  if (work.type === 'image') {
    return (!isLocalPreviewUrl(work.thumbnailUrl) && !!work.thumbnailUrl) ||
      Boolean(work.images?.some((image) => !isLocalPreviewUrl(image)));
  }

  return (!isLocalPreviewUrl(work.thumbnailUrl) && !!work.thumbnailUrl) ||
    (!isLocalPreviewUrl(work.videoUrl) && !!work.videoUrl);
};

const prunePendingWorkUploads = () => {
  let changed = false;

  pendingWorkUploads.forEach((work, postId) => {
    if (!work.uploadState && hasLocalPreviewMedia(work)) {
      pendingWorkUploads.delete(postId);
      changed = true;
      return;
    }

    if (!work.uploadState && !hasRenderableMedia(work)) {
      pendingWorkUploads.delete(postId);
      changed = true;
    }
  });

  if (changed) {
    persistPendingWorkUploads();
    emitPendingWorkUploadsChanged();
  }
};

const mapComment = (comment: BackendComment): WorkDetail['comments'][number] => {
  const author = comment.author || {
    userId: 'unknown',
    displayName: '匿名用户',
    avatarUrl: '',
    location: ''
  };

  return {
    id: comment.commentId,
    authorId: author.userId,
    avatar: resolveAvatarUrl(author.avatarUrl),
    username: author.displayName || author.userId || '匿名用户',
    content: comment.content,
    time: formatDate(comment.createdAt),
    location: author.location || '',
    likes: comment.likeCount,
    liked: !!comment.liked,
    replies: (comment.replies || []).map(mapComment)
  };
};

const getCurrentCommentAuthor = async () => {
  if (currentCommentAuthorCache) {
    return currentCommentAuthorCache;
  }

  const response = await userService.getUserProfile();
  if (!response.success || !response.data?.userProfile) {
    return null;
  }

  const userProfile = response.data.userProfile;
  currentCommentAuthorCache = {
    userId: userProfile.userId,
    displayName: userProfile.displayName || userProfile.username || userProfile.userId,
    avatarUrl: userProfile.avatarUrl,
    location: userProfile.location || ''
  };

  return currentCommentAuthorCache;
};

const mapCreatedComment = async (comment: CreateCommentResponse): Promise<WorkDetail['comments'][number]> => {
  if (!comment.author) {
    const currentAuthor = await getCurrentCommentAuthor();
    if (currentAuthor) {
      return mapComment({
        ...comment,
        author: currentAuthor
      });
    }
  }

  return mapComment(comment);
};

const mapCollaborator = (collaborator: {
  userId: string;
  displayName: string;
  username: string;
  avatarUrl?: string;
  verified?: boolean;
}): WorkCollaborator => ({
  id: collaborator.userId,
  name: collaborator.displayName || collaborator.username || collaborator.userId,
  avatar: resolveAvatarUrl(collaborator.avatarUrl),
  username: collaborator.username,
  verified: collaborator.verified
});

const mapCreatorSummary = (creator?: BackendPostSummary['creator']): WorkCreatorSummary | undefined => {
  if (!creator) {
    return undefined;
  }

  return {
    id: creator.userId,
    name: creator.displayName || creator.username || creator.userId,
    avatar: resolveAvatarUrl(creator.avatarUrl),
    username: creator.username,
    verified: creator.verified
  };
};

const mapBackendSpriteSheetConfig = (config?: BackendSpriteSheetConfig): SpriteSheetConfig | undefined => {
  if (!config) {
    return undefined;
  }

  return {
    columns: config.columns,
    rows: config.rows,
    frameCount: config.totalFrames,
    frameWidth: config.frameWidth,
    frameHeight: config.frameHeight,
    sheets: config.sheets
  };
};

const mapSummaryToWork = (post: BackendPostSummary): WorkSummary => ({
  id: post.postId,
  creatorUserId: post.creatorUserId,
  type: post.postType,
  videoUrl: post.postType === 'video' ? post.mediaUrl : undefined,
  images: post.postType === 'image' ? (post.images && post.images.length > 0 ? post.images : post.mediaUrl ? [post.mediaUrl] : []) : undefined,
  thumbnailUrl: post.thumbnailUrl || (post.postType === 'image' ? (post.images?.[0] || post.mediaUrl || '') : ''),
  title: post.title,
  description: post.description,
  tags: post.tags || [],
  likes: post.stats.likeCount || 0,
  views: post.stats.viewCount || 0,
  favorites: post.stats.bookmarkCount || 0,
  commentsCount: post.stats.commentCount || 0,
  createdAt: formatDate(post.createdAt),
  createdAtTimestamp: post.createdAt,
  isPinned: post.pinned,
  pinnedAt: post.pinnedAt,
  auditStatus: post.auditStatus,
  visibility: post.visibility,
  status: post.status,
  liked: post.userInteraction?.liked,
  bookmarked: post.userInteraction?.bookmarked,
  creator: mapCreatorSummary(post.creator),
  duration: post.duration,
  spriteSheetUrl: post.spriteSheetUrl,
  spriteSheetConfig: post.spriteSheetConfig
});

const mapDetailToWork = (
  detail: BackendPostDetailResponse,
  comments: BackendComment[]
): WorkDetail => {
  const images = detail.media.filter((item) => item.mediaType === 'image').map((item) => item.fileUrl);
  const primaryVideo = detail.media.find((item) => item.mediaType === 'video');

  return {
    id: detail.post.postId,
    type: detail.post.postType,
    videoUrl: primaryVideo?.fileUrl,
    images: images.length > 0 ? images : undefined,
    thumbnailUrl: detail.post.thumbnailUrl || primaryVideo?.thumbnailUrl || images[0] || '',
    title: detail.post.title,
    description: detail.post.description,
    tags: detail.post.tags || [],
    likes: detail.stats.likeCount || 0,
    views: detail.stats.viewCount || 0,
    favorites: detail.stats.bookmarkCount || 0,
    comments: comments.map(mapComment),
    commentCount: detail.stats.commentCount || comments.length,
    createdAt: formatDate(detail.post.publishedAt || detail.post.createdAt),
    author: {
      id: detail.creator.userId,
      name: detail.creator.displayName || detail.creator.username || detail.creator.userId,
      avatar: resolveAvatarUrl(detail.creator.avatarUrl)
    },
    collaborators: (detail.collaborators || []).map(mapCollaborator),
    isPinned: detail.post.pinned,
    pinnedAt: detail.post.pinnedAt,
    liked: detail.userInteraction?.liked,
    bookmarked: detail.userInteraction?.bookmarked,
    visibility: detail.post.visibility,
    status: detail.post.status,
    auditStatus: detail.post.status === 'rejected'
      ? 'REJECTED'
      : detail.post.status === 'active'
        ? 'APPROVED'
        : 'PENDING'
  } as WorkDetail;
};

const getOrCreateViewSessionId = () => {
  const storageKey = 'craft_cloud_post_view_session';
  const existing = localStorage.getItem(storageKey);
  if (existing) return existing;

  const created = `session_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  localStorage.setItem(storageKey, created);
  return created;
};

const buildPostPayload = (
  postData: WorkDraftPayload,
  includePostType: boolean,
  submitMode: WorkSubmitMode
) => ({
  title: postData.title,
  description: postData.description,
  ...(includePostType ? { postType: (postData.workType || 'video') as WorkType } : {}),
  visibility: submitMode === 'draft' ? 'draft' : 'public',
  tags: postData.tags || [],
  categories: postData.categories || [],
  metadata: {
    collaborators: (postData.collaborators || []).map((item) => item.id)
  }
});

const uploadPublishCover = async (file: File): Promise<string> => {
  const result = await uploadMedia(file, undefined, () => undefined, {
    altText: 'post-cover'
  });

  const thumbnailUrl = result?.thumbnailUrl || result?.fileUrl;
  if (!thumbnailUrl) {
    throw new Error('作品封面保存失败');
  }

  return thumbnailUrl;
};

const saveUploadedCover = async (postId: string, file: File) => {
  const thumbnailUrl = await uploadPublishCover(file);
  const coverUpdate = await postService.updatePost(postId, { thumbnailUrl });

  if (!coverUpdate.success) {
    throw new Error(coverUpdate.error || '作品封面保存失败');
  }
};

const uploadWorkMediaInBackground = async (
  postId: string,
  workType: WorkType,
  mediaFiles: File[],
  postData: WorkDraftPayload,
  onProgress: ProgressCallback
) => {
  if (workType === 'video') {
    const uploadResult = await uploadMedia(mediaFiles[0], postId, onProgress, { displayOrder: 0 });
    if (!uploadResult?.mediaId) {
      throw new Error('作品素材上传失败');
    }
    return;
  }

  const orderedFiles = getOrderedImageFiles(postData.imageFiles, postData.coverImageIndex);
  for (let index = 0; index < orderedFiles.length; index += 1) {
    const item = orderedFiles[index];
    const uploadResult = await uploadMedia(item.file, postId, (progress) => {
      const aggregatePercent = Math.min(
        100,
        Math.round(((index + progress.percent / 100) / orderedFiles.length) * 100)
      );
      onProgress({
        ...progress,
        percent: aggregatePercent
      });
    }, { displayOrder: index });

    if (!uploadResult?.mediaId) {
      throw new Error('作品素材上传失败');
    }
  }
};

const uploadWorkMediaSync = async (
  postId: string,
  workType: WorkType,
  mediaFiles: File[],
  postData: WorkDraftPayload,
  onProgress: ProgressCallback
) => {
  if (workType === 'video') {
    const uploadResult = await uploadMedia(mediaFiles[0], postId, onProgress, { displayOrder: 0 });
    if (!uploadResult?.mediaId) {
      throw new Error('作品素材上传失败');
    }
    return;
  }

  const orderedFiles = getOrderedImageFiles(postData.imageFiles, postData.coverImageIndex);
  for (let displayOrder = 0; displayOrder < orderedFiles.length; displayOrder += 1) {
    const item = orderedFiles[displayOrder];
    const uploadResult = await uploadMedia(item.file, postId, onProgress, { displayOrder });
    if (!uploadResult?.mediaId) {
      throw new Error('作品素材上传失败');
    }
  }
};

const getOrderedImageFiles = (imageFiles: File[] = [], coverImageIndex?: number) => {
  const indexedFiles = imageFiles.map((file, index) => ({ file, index }));

  if (
    typeof coverImageIndex !== 'number' ||
    coverImageIndex < 0 ||
    coverImageIndex >= indexedFiles.length
  ) {
    return indexedFiles;
  }

  return [
    indexedFiles[coverImageIndex],
    ...indexedFiles.filter((item) => item.index !== coverImageIndex)
  ];
};

const mapDetailResponseToSummary = (detail: BackendPostDetailResponse): WorkSummary => {
  const orderedMedia = [...detail.media].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
  const images = orderedMedia
    .filter((item) => item.mediaType === 'image')
    .map((item) => item.fileUrl);
  const primaryVideo = orderedMedia.find((item) => item.mediaType === 'video');
  const spriteSheet = primaryVideo?.metadata?.spriteSheet;
  const spriteSheetConfig = mapBackendSpriteSheetConfig(spriteSheet);

  return {
    id: detail.post.postId,
    creatorUserId: detail.creator.userId,
    type: detail.post.postType,
    videoUrl: detail.post.postType === 'video' ? primaryVideo?.fileUrl : undefined,
    images: detail.post.postType === 'image' ? (images.length > 0 ? images : undefined) : undefined,
    thumbnailUrl: detail.post.thumbnailUrl || primaryVideo?.thumbnailUrl || images[0] || '',
    title: detail.post.title,
    description: detail.post.description,
    tags: detail.post.tags || [],
    likes: detail.stats.likeCount || 0,
    views: detail.stats.viewCount || 0,
    favorites: detail.stats.bookmarkCount || 0,
    commentsCount: detail.stats.commentCount || 0,
    createdAt: formatDate(detail.post.createdAt),
    createdAtTimestamp: detail.post.createdAt,
    isPinned: detail.post.pinned,
    pinnedAt: detail.post.pinnedAt,
    auditStatus: detail.post.status === 'rejected'
      ? 'REJECTED'
      : detail.post.status === 'active'
        ? 'APPROVED'
        : 'PENDING',
    visibility: detail.post.visibility,
    status: detail.post.status,
    liked: detail.userInteraction?.liked,
    bookmarked: detail.userInteraction?.bookmarked,
    collaborators: (detail.collaborators || []).map(mapCollaborator),
    creator: {
      id: detail.creator.userId,
      name: detail.creator.displayName || detail.creator.username || detail.creator.userId,
      avatar: resolveAvatarUrl(detail.creator.avatarUrl),
      username: detail.creator.username,
      verified: detail.creator.verified
    },
    duration: primaryVideo?.duration,
    spriteSheetUrl: spriteSheet?.sheets?.[0]?.url,
    spriteSheetConfig
  };
};

const buildOptimisticWorkSummary = (
  postId: string,
  postData: WorkDraftPayload,
  submitMode: WorkSubmitMode,
  creator?: PublishWorkOptions['creator'],
  existingWork?: Partial<WorkSummary>
): WorkSummary => {
  const workType = (postData.workType || existingWork?.type || 'video') as WorkType;
  const localThumbnailUrl = postData.coverPreview || existingWork?.thumbnailUrl || '';
  const localImages = workType === 'image'
    ? (
      (postData.imageFiles?.length
        ? (localThumbnailUrl ? [localThumbnailUrl] : [])
        : existingWork?.images || (localThumbnailUrl ? [localThumbnailUrl] : []))
    )
    : undefined;

  return {
    id: postId,
    creatorUserId: existingWork?.creatorUserId || creator?.id || '',
    type: workType,
    videoUrl: workType === 'video'
      ? (postData.videoFile ? undefined : existingWork?.videoUrl)
      : undefined,
    images: localImages,
    thumbnailUrl: localThumbnailUrl,
    title: postData.title,
    description: postData.description,
    tags: postData.tags || existingWork?.tags || [],
    likes: existingWork?.likes || 0,
    views: existingWork?.views || 0,
    favorites: existingWork?.favorites || 0,
    commentsCount: existingWork?.commentsCount || 0,
    createdAt: existingWork?.createdAt || formatDate(Date.now()),
    createdAtTimestamp: existingWork?.createdAtTimestamp || Date.now(),
    isPinned: existingWork?.isPinned || false,
    pinnedAt: existingWork?.pinnedAt,
    auditStatus: existingWork?.auditStatus || 'PENDING',
    visibility: submitMode === 'draft' ? 'draft' : 'public',
    status: submitMode === 'draft'
      ? 'archived'
      : workType === 'image'
        ? 'active'
        : 'under_review',
    liked: existingWork?.liked || false,
    bookmarked: existingWork?.bookmarked || false,
    collaborators: postData.collaborators || existingWork?.collaborators || [],
    creator: existingWork?.creator || (creator ? {
      id: creator.id,
      name: creator.name,
      avatar: resolveAvatarUrl(creator.avatar)
    } : undefined),
    uploadState: 'uploading',
    uploadProgress: 0
  };
};

const mapUploadStatusToWorkUploadState = (status: UploadProgress['status']): WorkUploadState => {
  return status === 'processing' || status === 'completed' ? 'processing' : 'uploading';
};

const getPostSummaryById = async (postId: string): Promise<WorkSummary | null> => {
  try {
    const response = await apiClient.get<BackendPostDetailResponse>(`/api/v1/posts/${postId}`);
    if (!response.success || !response.data) {
      return null;
    }

    return mapDetailResponseToSummary(response.data);
  } catch (error) {
    console.error('Failed to get post summary by id:', error);
    return null;
  }
};

const hydratePendingWorkUpload = async (postId: string, attempts = 8, intervalMs = 1000): Promise<WorkSummary | null> => {
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const summary = await getPostSummaryById(postId);
    if (summary) {
      return summary;
    }

    await sleep(intervalMs);
  }

  return null;
};

const completePendingWorkUpload = async (
  postId: string,
  submitMode: WorkSubmitMode,
  creatorUserId?: string
) => {
  const existingPending = pendingWorkUploads.get(postId);
  if (existingPending) {
    updatePendingWorkUpload(postId, {
      uploadState: 'processing',
      uploadProgress: 100,
      visibility: submitMode === 'draft' ? 'draft' : 'public',
      status: submitMode === 'draft'
        ? 'archived'
        : existingPending.type === 'image'
          ? 'active'
          : 'under_review'
    });
  }

  if (isBrowser) {
    window.dispatchEvent(new Event('posts_updated'));
  }

  const hydratedWork = await hydratePendingWorkUpload(postId, 12, 1000);
  if (hydratedWork && hasRenderableMedia(hydratedWork)) {
    updatePendingWorkUpload(postId, {
      ...hydratedWork,
      uploadState: undefined,
      uploadProgress: undefined
    });
  } else if (!creatorUserId) {
    const pending = pendingWorkUploads.get(postId);
    if (pending && !hasRenderableMedia(pending)) {
      removePendingWorkUpload(postId);
    }
  }

  if (isBrowser) {
    window.dispatchEvent(new Event('posts_updated'));
    window.dispatchEvent(new CustomEvent(BACKGROUND_WORK_UPLOAD_COMPLETED_EVENT, {
      detail: {
        postId,
        submitMode,
        work: hydratedWork || pendingWorkUploads.get(postId)
      }
    }));
  }

  if (creatorUserId) {
    void (async () => {
      const synced = await waitForCreatorPostSync(postId, creatorUserId);
      if (synced) {
        removePendingWorkUpload(postId);
        window.dispatchEvent(new Event('posts_updated'));
        return;
      }

      const pending = pendingWorkUploads.get(postId);
      if (pending && !hasRenderableMedia(pending)) {
        removePendingWorkUpload(postId);
        window.dispatchEvent(new Event('posts_updated'));
      }
    })();
  }
};

const waitForCreatorPostSync = async (postId: string, creatorUserId?: string, attempts = 15, intervalMs = 2000) => {
  if (!creatorUserId) {
    return false;
  }

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      const response = await apiClient.get<{ posts: BackendPostSummary[] }>(
        `/api/v1/posts?creatorUserId=${encodeURIComponent(creatorUserId)}&limit=100`
      );

      const posts = response.data?.posts || [];
      if (posts.some((post) => post.postId === postId)) {
        return true;
      }
    } catch (error) {
      console.error('Failed to sync background work upload:', error);
    }

    await sleep(intervalMs);
  }

  return false;
};

export const postService = {
  getPendingWorkUploads: (creatorUserId?: string): WorkSummary[] => {
    prunePendingWorkUploads();

    const uploads = Array.from(pendingWorkUploads.values()).filter((item) => (
      !!item.uploadState || hasRenderableMedia(item)
    ));
    if (!creatorUserId) {
      return uploads;
    }

    return uploads.filter((item) => item.creatorUserId === creatorUserId);
  },

  getCreatorPosts: async (creatorUserId: string, limit = 50): Promise<WorkSummary[]> => {
    try {
      const response = await apiClient.get<{ posts: BackendPostSummary[] }>(
        `/api/v1/posts?creatorUserId=${encodeURIComponent(creatorUserId)}&limit=${limit}`
      );

      if (!response.success || !response.data?.posts) {
        return [];
      }

      const works = response.data.posts
        .map(mapSummaryToWork)
        .filter((work) => {
          // WAITING_UPLOAD = server hasn't received valid media yet.
          // Used to be hidden by the hasRenderableMedia gate, which
          // made the creator's own post invisible until moderation
          // started — even though the row exists with status
          // PENDING_UPLOAD on the server. Keep them visible so the
          // owner sees their orphaned uploads with a 上传未完成
          // indicator instead of nothing.
          if (work.auditStatus === 'WAITING_UPLOAD') return true;
          const pending = pendingWorkUploads.get(String(work.id));
          return hasRenderableMedia(work) || !!pending;
        });
      const removablePendingPostIds = works
        .filter((work) => {
          const pending = pendingWorkUploads.get(String(work.id));
          if (!pending) {
            return false;
          }

          if (hasRenderableMedia(work)) {
            return true;
          }

          return !pending.uploadState && hasPendingWorkSynced(pending, work);
        })
        .map((work) => String(work.id));

      removePendingWorkUploads(removablePendingPostIds);
      return works;
    } catch (error) {
      console.error('Failed to get creator posts:', error);
      return [];
    }
  },

  getRecommendedPosts: async (limit = 20): Promise<WorkSummary[]> => {
    try {
      const response = await apiClient.get<{
        posts: BackendPostSummary[];
        cursor?: string | null;
        hasMore?: boolean;
      }>(`/api/v1/posts?limit=${limit}`, undefined, { suppressAuthPrompt: true });

      if (!response.success || !response.data?.posts) {
        return [];
      }

      return response.data.posts.map(mapSummaryToWork).filter(hasRenderableMedia);
    } catch (error) {
      console.error('Failed to get recommended posts:', error);
      return [];
    }
  },

  getHotPosts: async (limit = 20): Promise<WorkSummary[]> => {
    try {
      const response = await apiClient.get<any>(
        `/api/v1/discover/hot-posts?limit=${limit}`,
        undefined,
        { suppressAuthPrompt: true }
      );
      if (!response.success || !response.data) return [];
      // Handle both { posts: [...] } and { success: true, data: { posts: [...] } }
      const posts = response.data.posts || response.data.data?.posts || [];
      return posts.map(mapSummaryToWork).filter(hasRenderableMedia);
    } catch (error) {
      console.error('Failed to get hot posts:', error);
      return [];
    }
  },

  getFeaturedPosts: async (): Promise<WorkSummary[]> => {
    try {
      const response = await apiClient.get<any>(
        `/api/v1/discover/featured-posts`,
        undefined,
        { suppressAuthPrompt: true }
      );
      if (!response.success || !response.data) return [];
      const posts = response.data.posts || response.data.data?.posts || [];
      return posts.map(mapSummaryToWork).filter(hasRenderableMedia);
    } catch (error) {
      console.error('Failed to get featured posts:', error);
      return [];
    }
  },

  getInteractedPosts: async (
    interactionUserId: string,
    interactionType: PostInteractionType,
    limit = 50,
    options?: ApiRequestOptions
  ): Promise<WorkSummary[]> => {
    try {
      const response = await apiClient.get<{ posts: BackendPostSummary[] }>(
        `/api/v1/posts?interactionUserId=${encodeURIComponent(interactionUserId)}&interactionType=${interactionType}&limit=${limit}`,
        undefined,
        options
      );

      if (!response.success || !response.data?.posts) {
        return [];
      }

      return response.data.posts.map(mapSummaryToWork);
    } catch (error) {
      console.error(`Failed to get ${interactionType} posts:`, error);
      return [];
    }
  },

  getPostDetail: async (postId: string): Promise<WorkDetail | null> => {
    try {
      const encodedPostId = encodeURIComponent(postId);
      const [detailResponse, commentsResponse] = await Promise.all([
        apiClient.get<BackendPostDetailResponse>(`/api/v1/posts/${encodedPostId}`),
        apiClient.get<BackendCommentsResponse>(`/api/v1/posts/${encodedPostId}/comments?limit=50`)
      ]);

      if (!detailResponse.success || !detailResponse.data) {
        return null;
      }

      return mapDetailToWork(detailResponse.data, commentsResponse.data?.comments || []);
    } catch (error) {
      console.error('Failed to get post detail:', error);
      return null;
    }
  },

  createPost: async (postData: any, submitMode: WorkSubmitMode = 'publish') => {
    try {
      const response = await apiClient.post<any>('/api/v1/posts', buildPostPayload(postData, true, submitMode));
      if (response.success && response.data) {
        return { success: true, data: response.data };
      }

      return { success: false, error: response.error || 'Failed to create post' };
    } catch (error: any) {
      console.error('Create post failed:', error);
      return { success: false, error: error.message };
    }
  },

  updatePost: async (postId: string, data: Record<string, any>) => {
    return apiClient.put<any>(`/api/v1/posts/${postId}`, data);
  },

  publishWork: async (
    postData: WorkDraftPayload,
    onProgress: ProgressCallback = () => undefined,
    submitMode: WorkSubmitMode = 'publish',
    options: PublishWorkOptions = {}
  ) => {
    const workType = (postData.workType || 'video') as WorkType;
    const mediaFiles = workType === 'video'
      ? (postData.videoFile ? [postData.videoFile] : [])
      : (postData.imageFiles || []);

    if (mediaFiles.length === 0) {
      return { success: false, error: '请先上传作品素材' };
    }

    if (options.background) {
      const createResult = await postService.createPost(postData, submitMode);
      if (!createResult.success || !createResult.data?.postId) {
        return createResult;
      }

      const postId = createResult.data.postId as string;
      setPendingWorkUpload(buildOptimisticWorkSummary(postId, postData, submitMode, options.creator, options.existingWork));

      void (async () => {
        const rollbackCreatedPost = async (errorMessage: string) => {
          try {
            await postService.deletePost(postId);
          } catch (rollbackError) {
            console.error('Failed to rollback created post:', rollbackError);
          } finally {
            removePendingWorkUpload(postId);
            window.dispatchEvent(new Event('posts_updated'));
            window.dispatchEvent(new CustomEvent(BACKGROUND_WORK_UPLOAD_FAILED_EVENT, {
              detail: {
                postId,
                submitMode,
                error: errorMessage
              }
            }));
          }
        };

        try {
          const coverTask = postData.coverFile instanceof File
            ? saveUploadedCover(postId, postData.coverFile)
            : Promise.resolve();

          const mediaTask = uploadWorkMediaInBackground(
            postId,
            workType,
            mediaFiles,
            postData,
            (progress) => {
              updatePendingWorkUpload(postId, {
                uploadState: mapUploadStatusToWorkUploadState(progress.status),
                uploadProgress: progress.percent
              });
              onProgress(progress);
            }
          );

          await Promise.all([coverTask, mediaTask]);

          await completePendingWorkUpload(postId, submitMode, options.creator?.id || createResult.data?.creatorUserId);
        } catch (error: any) {
          console.error('Background publish work failed:', error);
          await rollbackCreatedPost(error.message || '作品发布失败');
        }
      })();

      return {
        success: true,
        data: createResult.data,
        queued: true
      };
    }

    const createResult = await postService.createPost(postData, submitMode);
    if (!createResult.success || !createResult.data?.postId) {
      return createResult;
    }

    const postId = createResult.data.postId as string;
    const rollbackCreatedPost = async () => {
      try {
        await postService.deletePost(postId);
      } catch (rollbackError) {
        console.error('Failed to rollback created post:', rollbackError);
      }
    };

    try {
      const coverTask = postData.coverFile instanceof File
        ? saveUploadedCover(postId, postData.coverFile)
        : Promise.resolve();
      const mediaTask = uploadWorkMediaSync(postId, workType, mediaFiles, postData, onProgress);

      await Promise.all([coverTask, mediaTask]);

      window.dispatchEvent(new Event('posts_updated'));
      return createResult;
    } catch (error: any) {
      console.error('Publish work failed:', error);
      await rollbackCreatedPost();
      return { success: false, error: error.message || '作品发布失败' };
    }
  },

  updateWork: async (
    postId: string,
    postData: WorkDraftPayload,
    submitMode: WorkSubmitMode = 'publish',
    options: PublishWorkOptions = {}
  ) => {
    const workType = (postData.workType || options.existingWork?.type || 'video') as WorkType;
    const mediaFiles = workType === 'video'
      ? (postData.videoFile ? [postData.videoFile] : [])
      : (postData.imageFiles || []);
    const hasMediaUploads = mediaFiles.length > 0;
    const hasCoverUpload = postData.coverFile instanceof File;

    if (options.background) {
      setPendingWorkUpload({
        ...buildOptimisticWorkSummary(postId, postData, submitMode, options.creator, options.existingWork),
        uploadState: hasMediaUploads || hasCoverUpload ? 'uploading' : 'processing',
        uploadProgress: hasMediaUploads || hasCoverUpload ? 0 : 100
      });

      void (async () => {
        const failBackgroundUpdate = (errorMessage: string) => {
          removePendingWorkUpload(postId);
          window.dispatchEvent(new Event('posts_updated'));
          window.dispatchEvent(new CustomEvent(BACKGROUND_WORK_UPLOAD_FAILED_EVENT, {
            detail: {
              postId,
              submitMode,
              error: errorMessage
            }
          }));
        };

        try {
          const syncCreatorUserId = options.existingWork ? undefined : options.creator?.id;

          const response = await postService.updatePost(postId, {
            ...buildPostPayload(postData, false, submitMode)
          });

          if (!response.success) {
            failBackgroundUpdate(response.error || '更新作品失败');
            return;
          }

          const coverTask = hasCoverUpload
            ? saveUploadedCover(postId, postData.coverFile as File)
            : Promise.resolve();

          const mediaTask = hasMediaUploads
            ? uploadWorkMediaInBackground(
              postId,
              workType,
              mediaFiles,
              postData,
              (progress) => {
                updatePendingWorkUpload(postId, {
                  uploadState: mapUploadStatusToWorkUploadState(progress.status),
                  uploadProgress: progress.percent
                });
              }
            )
            : Promise.resolve();

          await Promise.all([coverTask, mediaTask]);

          if (!hasMediaUploads && hasCoverUpload) {
            updatePendingWorkUpload(postId, {
              uploadState: 'processing',
              uploadProgress: 100
            });
          }

          await completePendingWorkUpload(postId, submitMode, syncCreatorUserId);
        } catch (error: any) {
          console.error('Background update work failed:', error);
          failBackgroundUpdate(error.message || '更新作品失败');
        }
      })();

      return {
        success: true,
        data: { postId },
        queued: true
      };
    }

    try {
      const response = await postService.updatePost(postId, {
        ...buildPostPayload(postData, false, submitMode)
      });

      if (!response.success) {
        return response;
      }

      const coverTask = postData.coverFile instanceof File
        ? saveUploadedCover(postId, postData.coverFile)
        : Promise.resolve();

      const mediaTask = hasMediaUploads
        ? uploadWorkMediaSync(postId, workType, mediaFiles, postData, () => undefined)
        : Promise.resolve();

      await Promise.all([coverTask, mediaTask]);

      if (response.success) {
        window.dispatchEvent(new Event('posts_updated'));
      }

      return response;
    } catch (error: any) {
      console.error('Update work failed:', error);
      return { success: false, error: error.message || '更新作品失败' };
    }
  },

  deletePost: async (postId: string) => {
    const response = await apiClient.delete<any>(`/api/v1/posts/${postId}`);
    if (response.success) {
      window.dispatchEvent(new Event('posts_updated'));
    }
    return response;
  },

  pinPost: async (postId: string) => {
    return apiClient.post<any>(`/api/v1/posts/${postId}/pin`);
  },

  unpinPost: async (postId: string) => {
    return apiClient.delete<any>(`/api/v1/posts/${postId}/pin`);
  },

  toggleLike: async (postId: string) => {
    return apiClient.post<{ liked: boolean }>(`/api/v1/posts/${encodeURIComponent(postId)}/like`);
  },

  toggleBookmark: async (postId: string) => {
    return apiClient.post<{ bookmarked: boolean }>(`/api/v1/posts/${encodeURIComponent(postId)}/bookmark`);
  },

  trackView: async (postId: string) => {
    const sessionId = getOrCreateViewSessionId();
    return apiClient.post<{ tracked: boolean }>(`/api/v1/posts/${encodeURIComponent(postId)}/view`, { sessionId });
  },

  getPostComments: async (postId: string, limit: number = 50) => {
    try {
      const response = await apiClient.get<BackendCommentsResponse>(
        `/api/v1/posts/${encodeURIComponent(postId)}/comments?limit=${limit}`
      );

      if (!response.success || !response.data) {
        return { success: false, error: response.error || 'Failed to get comments' };
      }

      return {
        success: true,
        data: {
          comments: (response.data.comments || []).map(mapComment),
          totalCount: response.data.totalCount ?? response.data.comments?.length ?? 0
        }
      };
    } catch (error: any) {
      console.error('Failed to get post comments:', error);
      return { success: false, error: error.message || 'Failed to get comments' };
    }
  },

  createComment: async (postId: string, content: string, parentCommentId?: string) => {
    const response = await apiClient.post<CreateCommentResponse>(
      `/api/v1/posts/${encodeURIComponent(postId)}/comments`,
      { content, parentCommentId }
    );

    if (!response.success || !response.data) {
      return response;
    }

    return {
      ...response,
      data: await mapCreatedComment(response.data)
    };
  },

  toggleCommentLike: async (postId: string, commentId: string) => {
    return apiClient.post<{ liked: boolean; likeCount: number }>(
      `/api/v1/posts/${encodeURIComponent(postId)}/comments/${encodeURIComponent(commentId)}/like`
    );
  },

  deleteComment: async (postId: string, commentId: string) => {
    return apiClient.delete<DeleteCommentResponse>(
      `/api/v1/posts/${encodeURIComponent(postId)}/comments/${encodeURIComponent(commentId)}`
    );
  },

  checkModerationBatch: async (postIds: string[]): Promise<Record<string, string>> => {
    const res = await apiClient.post<Record<string, string>>('/api/v1/posts/check-moderation-batch', { postIds });
    return res.success ? (res.data || {}) : {};
  }
};
